import { translate } from "@/i18n";
import { desktopShellMessages } from "@/i18n/messages/desktop-shell";
import { useInputBarQuestionStore } from "@/stores/input-bar-question-store";
import { listPendingAwaitingInputs } from "@/api/awaiting-input";
import { getNotificationSummary } from "@/api/notifications";
import {
  fetchAllActiveExecutions,
  getExecutionSnapshot,
  type ActiveExecutionResponse,
} from "@/api/executions";
import type { WsMessage } from "@/api/websocket";
import { seedWorkToastApprovals } from "@/lib/seed-work-toast-approvals";
import { seedWorkToastQuestions } from "@/lib/seed-work-toast-questions";
import { workToastExecutionId } from "@/lib/computer-toast-key";
import { settleComputerUseToast } from "@/lib/computer-use-toast-feed";
import { isWorkToastQuestionAnswered } from "@/lib/work-toast-question-delivery";
import { applyWorkToastExecutionEvent } from "@/lib/work-toast-execution-events";
import { createWorkToastItem } from "@/lib/work-toast-item";
import type { FrameApplyContext } from "@/lib/companion-frame-reducer";
import { isAttentionState, isTerminalState } from "@/components/toasts/toast-status";
import { useAgentStore } from "@/stores/agent-store";
import { useCanvasStore } from "@/stores/canvas-store";
import { useCompanionStore, type ActiveExecution, type ExecutionStatus } from "@/stores/companion-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useProjectStore } from "@/stores/project-store";
import { useTasksStore } from "@/stores/tasks-store";
import { useWorkToastStore, type WorkToastSource } from "@/stores/work-toast-store";
import { hydratePendingAwaitingInputs } from "./pending-awaiting-input-hydration";

const REQUEST_TIMEOUT_MS = 10_000;
const toastContext = {
  workToastStore: useWorkToastStore,
  agentStore: useAgentStore,
  projectStore: useProjectStore,
  tasksStore: useTasksStore,
  notificationStore: useNotificationStore,
  onboardingStore: useOnboardingStore,
} as FrameApplyContext;

function activeExecution(row: ActiveExecutionResponse): ActiveExecution {
  return {
    id: row.id,
    nativeSubagent: row.native_subagent === true,
    status: row.status as ExecutionStatus,
    runtimeSessionId: row.runtime_session_id ?? undefined,
    conversationId: row.conversation_id,
    agentName: row.agent_name ?? undefined,
    agentKey: row.agent_key ?? undefined,
    spaceId: row.project_id,
    taskId: row.task_id ?? undefined,
    startedAt: row.started_at,
    todos: [],
    origin: row.origin,
  };
}

function hydrateExecutionToast(row: ActiveExecutionResponse, fetchedAt: number) {
  const store = useWorkToastStore.getState();
  const existing = store.items[row.id];
  const fields = {
    tracked: true,
    source: (row.native_subagent ? "native_worker" : "agent") as WorkToastSource,
    conversationId: row.conversation_id || existing?.conversationId || null,
    projectId: row.project_id ?? existing?.projectId ?? null,
    taskId: row.task_id ?? existing?.taskId ?? null,
  };
  const state = row.status === "paused" ? "stopped" : "working";
  // A paused run settled when it paused, not when this snapshot arrived, so a
  // stale stop never outranks newer work in the same conversation.
  const pausedAt = state === "stopped" && row.paused_at ? Date.parse(row.paused_at) : NaN;
  const stateChangedAt = Number.isFinite(pausedAt) ? pausedAt : fetchedAt - 1;
  // Preserve attention, dismissal and observations newer than the snapshot,
  // while repairing an older stopped/done card after a missed resume event.
  if (existing) {
    const preserveState = isAttentionState(existing.state) || existing.stateChangedAt >= fetchedAt;
    store.patchItem(row.id, {
      ...fields,
      ...(!preserveState && existing.state !== state ? {
        state, stateChangedAt, question: null, stateBeforeBlocked: null,
      } : {}),
    });
    return;
  }
  store.upsertItem(createWorkToastItem({
    key: row.id,
    state,
    agentName: row.native_subagent ? "Denker" : row.agent_name ?? "Denker",
    detail: row.task_title ?? (row.status === "waiting_approval" ? translate(desktopShellMessages, "waitingForApproval") : ""),
    ...fields,
  }, stateChangedAt));
}

/** REST restores missed state without delaying the socket or rolling back live frames. */
export function createOwnerWorkReconciler(isCurrent: () => boolean) {
  let active: {
    abort: AbortController;
    timer: ReturnType<typeof setTimeout>;
    executions: Set<string>;
    questions: Set<string>;
    questionExecutions: Set<string>;
  } | null = null;

  const cancel = () => {
    if (!active) return;
    clearTimeout(active.timer);
    active.abort.abort();
    active = null;
  };

  const observe = (message: WsMessage) => {
    if (!active) return;
    const data = message.data;
    // Progress/cursor frames do not carry authoritative lifecycle state. They
    // must not prevent restoring an execution whose running event was missed.
    if (message.type === "execution.status" && typeof data.execution_id === "string") {
      active.executions.add(data.execution_id);
    }
    if (typeof data.awaiting_input_id === "string") {
      active.questions.add(data.awaiting_input_id);
      if (typeof data.execution_id === "string") active.questionExecutions.add(data.execution_id);
    }
  };

  const refresh = async (projectId: string | undefined) => {
    cancel();
    const abort = new AbortController();
    const request = {
      abort,
      timer: setTimeout(() => abort.abort(), REQUEST_TIMEOUT_MS),
      executions: new Set<string>(),
      questions: new Set<string>(),
      questionExecutions: new Set<string>(),
    };
    active = request;
    const fetchedAt = Date.now();
    const initialExecutions = useCompanionStore.getState().executions;
    const initialItems = useWorkToastStore.getState().items;
    const initialCanvas = useCanvasStore.getState();
    const selectedQuestion = useInputBarQuestionStore.getState().question;
    const current = () => active === request && !abort.signal.aborted && isCurrent();
    const unchanged = (id: string) => current() &&
      !request.executions.has(id) && !request.questionExecutions.has(id);

    const executionRefresh = fetchAllActiveExecutions(abort.signal).then(async (rows) => {
      if (!current()) return;
      const live = useCompanionStore.getState().executions;
      const next = new Map<string, ActiveExecution>();
      for (const row of rows) {
        const liveEntry = live.get(row.id);
        const before = initialExecutions.get(row.id);
        const changed = request.executions.has(row.id) ||
          (liveEntry !== undefined && liveEntry.status !== before?.status);
        const entry = changed ? liveEntry : { ...liveEntry, ...activeExecution(row) };
        if (entry) next.set(row.id, entry);
        if (!changed) hydrateExecutionToast(row, fetchedAt);
      }
      for (const [id, entry] of live) {
        if (!next.has(id) && (request.executions.has(id) || entry !== initialExecutions.get(id))) {
          next.set(id, entry);
        }
      }
      useCompanionStore.getState().syncAllExecutions([...next.values()]);

      const activeIds = new Set(rows.map((row) => row.id));
      // An absent active row is not proof of success. Fetch only previously
      // observed nonterminal cards, in small batches, to recover the outcome.
      const missing = Object.values(initialItems).filter((item) =>
        item.tracked && item.projectId && !isTerminalState(item.state) &&
        (item.source === "agent" || item.source === "native_worker") && !activeIds.has(item.key),
      );
      for (let offset = 0; offset < missing.length && current(); offset += 4) {
        await Promise.allSettled(missing.slice(offset, offset + 4).map(async (item) => {
          if (!unchanged(item.key)) return;
          let result: { status: string; error_message?: string | null };
          try {
            result = await getExecutionSnapshot(item.projectId!, item.key, abort.signal);
          } catch (error) {
            // The run is gone server-side (cleaned up, never persisted): a
            // working pill for it is a ghost no stop can clear. Remove it —
            // nothing says it failed or was stopped. Anything but 404
            // (offline, abort) leaves the pill alone for the next pass.
            if ((error as { status?: unknown } | null)?.status !== 404) return;
            if (!unchanged(item.key)) return;
            const toast = useWorkToastStore.getState().items[item.key];
            if (!toast || toast.dismissed || toast.state !== "working") return;
            useWorkToastStore.getState().removeItem(item.key);
            return;
          }
          if (!unchanged(item.key)) return;
          applyWorkToastExecutionEvent({ type: "execution.status", data: {
            execution_id: item.key, status: result.status, error: result.error_message,
          } }, toastContext, fetchedAt - 1);
        }));
      }
      // Standalone computer cards are keyed by `computer:<execution>` and are
      // not part of the snapshot path above. Settle cards whose execution disappeared.
      for (const item of Object.values(initialItems)) {
        if (item.source !== "computer" || !item.tracked || item.state !== "working") continue;
        const id = workToastExecutionId(item);
        if (activeIds.has(id) || !unchanged(id)) continue;
        settleComputerUseToast(id, fetchedAt - 1);
      }
    });

    const questionRefresh = projectId
      ? listPendingAwaitingInputs(projectId, abort.signal).then((inputs) => {
          if (!current() || useProjectStore.getState().currentProject?.id !== projectId) return;
          const canApply = (executionId: string, questionId: string) => {
            if (!unchanged(executionId) || request.questions.has(questionId)) return false;
            const before = initialItems[executionId];
            const now = useWorkToastStore.getState().items[executionId];
            return !before?.question || now?.question?.awaitingInputId === before.question.awaitingInputId;
          };
          const pendingIds = new Set(inputs.filter((input) => input.status === "pending").map((input) => input.id));
          const canvas = useCanvasStore.getState();
          for (const [frameId, items] of initialCanvas.awaitingInputs) {
            for (const item of items) {
              const scope = initialItems[item.executionId]?.projectId ?? initialExecutions.get(item.executionId)?.spaceId;
              if (scope === projectId && !pendingIds.has(item.id) && canApply(item.executionId, item.id) && canvas.awaitingInputs.get(frameId)?.includes(item)) {
                canvas.removeAwaitingInput(item.id);
              }
            }
          }
          for (const [id, cursor] of initialCanvas.agentCursors) {
            const questionId = cursor.question?.awaitingInputId;
            if (cursor.spaceId === projectId && questionId && !pendingIds.has(questionId) &&
                canApply(cursor.executionId ?? "", questionId) && canvas.agentCursors.get(id)?.question === cursor.question) {
              canvas.clearAgentCursorQuestion(id);
            }
          }
          const safeInputs = inputs.filter((input) => input.status === "pending" &&
            !isWorkToastQuestionAnswered(input.id) && canApply(input.execution, input.id));
          hydratePendingAwaitingInputs(safeInputs, projectId, canvas);
          // A project switch clears the canvas map before this authoritative
          // list returns. Resolve a retained selected card only after this list,
          // never merely because the temporary map is empty.
          const selected = selectedQuestion?.awaiting;
          if (selected?.projectId === projectId &&
              useInputBarQuestionStore.getState().question === selectedQuestion) {
            for (const candidate of [selected, ...(selectedQuestion?.remaining ?? [])]) {
              if (!pendingIds.has(candidate.id) && canApply(candidate.executionId, candidate.id)) canvas.removeAwaitingInput(candidate.id);
            }
          }
          seedWorkToastQuestions(inputs, projectId, fetchedAt, canApply);
        })
      : Promise.resolve();

    // Approvals wait in every project, so they are restored with or without
    // one open.
    const approvalRefresh = getNotificationSummary(abort.signal).then((summary) => {
      if (!current()) return;
      seedWorkToastApprovals(summary.approvals, toastContext, fetchedAt);
    });
    await Promise.allSettled([executionRefresh, questionRefresh, approvalRefresh]);
    clearTimeout(request.timer);
    if (active === request) active = null;
  };
  return { refresh, observe, dispose: cancel };
}
